import { useState, useCallback, useRef, useEffect } from 'react'
import { chatApi, ChatMessage, ChatRequest } from '../lib/chatApi'
// import { useWebSocket } from './useWebSocket'

interface ChatState {
  messages: ChatMessage[]
  isLoading: boolean
  error: string | null
  isTyping: boolean
  currentResponse: string
  suggestions: string[]
}

interface UseChatOptions {
  projectId: string
  vendorResponseId: string
  userId: string
  autoConnect?: boolean
  useWebSocket?: boolean
}

export const useChat = (options: UseChatOptions) => {
  const { projectId, vendorResponseId, userId, autoConnect = true, useWebSocket = true } = options
  
  const defaultSuggestions = [
    'What are the main strengths of this proposal?',
    'What requirements are not fully met?',
    'What documents or information are missing?'
  ]
  
  const [state, setState] = useState<ChatState>({
    messages: [
      {
        message_id: 'welcome',
        role: 'assistant',
        content: 'Hello! I\'m your AI assistant. I can help you analyze this vendor response, compare it with others, and draft follow-up communications. What would you like to know?',
        timestamp: new Date().toISOString(),
        suggestions: defaultSuggestions
      }
    ],
    isLoading: false,
    error: null,
    isTyping: false,
    currentResponse: '',
    suggestions: defaultSuggestions
  })

  const messagesRef = useRef<ChatMessage[]>(state?.messages || [])
  const conversationIdRef = useRef<string | null>(null)

  // WebSocket connection (temporarily disabled)
  const isConnected = false
  const sendWebSocketMessage = useCallback(async (message: string) => {
    console.log('WebSocket temporarily disabled, falling back to HTTP')
    return false
  }, [])

  // Send message via WebSocket
  const sendMessageWebSocket = useCallback(async (message: string) => {
    if (!isConnected) {
      setState(prev => ({ ...prev, error: 'Not connected to chat service' }))
      return
    }

    const userMessage: ChatMessage = {
      message_id: `user_${Date.now()}`,
      role: 'user',
      content: message,
      timestamp: new Date().toISOString()
    }

    const newMessages = [...messagesRef.current, userMessage]
    messagesRef.current = newMessages

    setState(prev => ({
      ...prev,
      messages: newMessages,
      isLoading: true,
      error: null,
      currentResponse: ''
    }))

    const success = sendWebSocketMessage({
      message,
      project_id: projectId,
      vendor_response_id: vendorResponseId,
      conversation_id: conversationIdRef.current
    })

    if (!success) {
      setState(prev => ({
        ...prev,
        error: 'Failed to send message',
        isLoading: false
      }))
    }
  }, [isConnected, sendWebSocketMessage, projectId, vendorResponseId])

  // Send message via HTTP API
  const sendMessageHttp = useCallback(async (message: string) => {
    const userMessage: ChatMessage = {
      message_id: `user_${Date.now()}`,
      role: 'user',
      content: message,
      timestamp: new Date().toISOString()
    }

    const newMessages = [...messagesRef.current, userMessage]
    messagesRef.current = newMessages

    setState(prev => ({
      ...prev,
      messages: newMessages,
      isLoading: true,
      error: null
    }))

    try {
      const request: ChatRequest = {
        message,
        project_id: projectId,
        vendor_response_id: vendorResponseId,
        conversation_id: conversationIdRef.current || undefined
      }

      const response = await chatApi.sendMessage(request)
      
      console.log('Chat API response:', response)
      
      if (!response) {
        throw new Error('No response from chat API')
      }
      
      // Create a properly formatted message if response is missing fields
      const formattedResponse = {
        message_id: response.message_id || `ai_${Date.now()}`,
        role: response.role || 'assistant',
        content: response.content || 'I apologize, but I encountered an issue processing your request.',
        timestamp: response.timestamp || new Date().toISOString(),
        suggestions: response.suggestions || []
      }
      
      const finalMessages = [...newMessages, formattedResponse]
      messagesRef.current = finalMessages

      setState(prev => ({
        ...prev,
        messages: finalMessages,
        isLoading: false,
        suggestions: Array.isArray(formattedResponse.suggestions) ? formattedResponse.suggestions : []
      }))

    } catch (error) {
      console.error('Error sending message:', error)
      setState(prev => ({
        ...prev,
        error: 'Failed to send message',
        isLoading: false
      }))
    }
  }, [projectId, vendorResponseId])

  // Send message via streaming API
  const sendMessageStream = useCallback(async (message: string) => {
    const userMessage: ChatMessage = {
      message_id: `user_${Date.now()}`,
      role: 'user',
      content: message,
      timestamp: new Date().toISOString()
    }

    const newMessages = [...messagesRef.current, userMessage]
    messagesRef.current = newMessages

    setState(prev => ({
      ...prev,
      messages: newMessages,
      isLoading: true,
      error: null,
      currentResponse: '',
      isTyping: true
    }))

    try {
      let fullResponse = ''

      await chatApi.streamChatResponse(
        projectId,
        vendorResponseId,
        message,
        (chunk) => {
          fullResponse += chunk
          setState(prev => ({
            ...prev,
            currentResponse: fullResponse,
            isTyping: true
          }))
        },
        () => {
          const assistantMessage: ChatMessage = {
            message_id: `stream_${Date.now()}`,
            role: 'assistant',
            content: fullResponse,
            timestamp: new Date().toISOString()
          }

          const finalMessages = [...newMessages, assistantMessage]
          messagesRef.current = finalMessages

          setState(prev => ({
            ...prev,
            messages: finalMessages,
            isLoading: false,
            isTyping: false,
            currentResponse: ''
          }))
        },
        (error) => {
          setState(prev => ({
            ...prev,
            error,
            isLoading: false,
            isTyping: false,
            currentResponse: ''
          }))
        }
      )

    } catch (error) {
      console.error('Error streaming message:', error)
      setState(prev => ({
        ...prev,
        error: 'Failed to send message',
        isLoading: false,
        isTyping: false
      }))
    }
  }, [projectId, vendorResponseId])

  // Main send message function
  const sendMessage = useCallback(async (message: string) => {
    if (!message.trim()) return

    if (useWebSocket && isConnected) {
      await sendMessageWebSocket(message)
    } else {
      // Fallback to HTTP API (streaming disabled temporarily)
      await sendMessageHttp(message)
    }
  }, [useWebSocket, isConnected, sendMessageWebSocket, sendMessageHttp])

  // Clear conversation
  const clearConversation = useCallback(() => {
    messagesRef.current = []
    conversationIdRef.current = null
    setState(prev => ({
      ...prev,
      messages: [],
      error: null,
      isTyping: false,
      currentResponse: '',
      suggestions: []
    }))
  }, [])


  // Draft follow-up email
  const draftFollowUpEmail = useCallback(async () => {
    // Add user action message to chat
    const userMessage: ChatMessage = {
      message_id: `user_${Date.now()}`,
      role: 'user',
      content: 'Draft follow-up email for missing items',
      timestamp: new Date().toISOString()
    }

    const newMessages = [...messagesRef.current, userMessage]
    messagesRef.current = newMessages

    setState(prev => ({
      ...prev,
      messages: newMessages,
      isLoading: true,
      error: null
    }))

    try {
      const response = await chatApi.draftFollowUpEmail(vendorResponseId)
      
      if (!response) {
        throw new Error('No response from draft email API')
      }
      
      // Create a properly formatted message if response is missing fields
      const formattedResponse = {
        message_id: response.message_id || `email_${Date.now()}`,
        role: response.role || 'assistant',
        content: response.content || 'I apologize, but I encountered an issue drafting the email.',
        timestamp: response.timestamp || new Date().toISOString(),
        suggestions: response.suggestions || []
      }
      
      const finalMessages = [...newMessages, formattedResponse]
      messagesRef.current = finalMessages

      setState(prev => ({
        ...prev,
        messages: finalMessages,
        isLoading: false,
        suggestions: Array.isArray(formattedResponse.suggestions) ? formattedResponse.suggestions : []
      }))

    } catch (error) {
      console.error('Error drafting email:', error)
      setState(prev => ({
        ...prev,
        error: 'Failed to draft email',
        isLoading: false
      }))
    }
  }, [vendorResponseId])

  // Summarize qualifications
  const summarizeQualifications = useCallback(async () => {
    // Add user action message to chat
    const userMessage: ChatMessage = {
      message_id: `user_${Date.now()}`,
      role: 'user',
      content: 'Summarize key qualifications',
      timestamp: new Date().toISOString()
    }

    const newMessages = [...messagesRef.current, userMessage]
    messagesRef.current = newMessages

    setState(prev => ({
      ...prev,
      messages: newMessages,
      isLoading: true,
      error: null
    }))

    try {
      const response = await chatApi.summarizeQualifications(projectId, vendorResponseId)
      
      if (!response) {
        throw new Error('No response from summarize qualifications API')
      }
      
      // Create a properly formatted message if response is missing fields
      const formattedResponse = {
        message_id: response.message_id || `qualifications_${Date.now()}`,
        role: response.role || 'assistant',
        content: response.content || 'I apologize, but I encountered an issue summarizing qualifications.',
        timestamp: response.timestamp || new Date().toISOString(),
        suggestions: response.suggestions || []
      }
      
      const finalMessages = [...newMessages, formattedResponse]
      messagesRef.current = finalMessages

      setState(prev => ({
        ...prev,
        messages: finalMessages,
        isLoading: false,
        suggestions: Array.isArray(formattedResponse.suggestions) ? formattedResponse.suggestions : []
      }))

    } catch (error) {
      console.error('Error summarizing qualifications:', error)
      setState(prev => ({
        ...prev,
        error: 'Failed to summarize qualifications',
        isLoading: false
      }))
    }
  }, [projectId, vendorResponseId])

  // List missing requirements
  const listMissingRequirements = useCallback(async () => {
    // Add user action message to chat
    const userMessage: ChatMessage = {
      message_id: `user_${Date.now()}`,
      role: 'user',
      content: 'List missing requirements',
      timestamp: new Date().toISOString()
    }

    const newMessages = [...messagesRef.current, userMessage]
    messagesRef.current = newMessages

    setState(prev => ({
      ...prev,
      messages: newMessages,
      isLoading: true,
      error: null
    }))

    try {
      const response = await chatApi.listMissingRequirements(projectId, vendorResponseId)
      
      if (!response) {
        throw new Error('No response from list missing requirements API')
      }
      
      // Create a properly formatted message if response is missing fields
      const formattedResponse = {
        message_id: response.message_id || `missing_requirements_${Date.now()}`,
        role: response.role || 'assistant',
        content: response.content || 'I apologize, but I encountered an issue listing missing requirements.',
        timestamp: response.timestamp || new Date().toISOString(),
        suggestions: response.suggestions || []
      }
      
      const finalMessages = [...newMessages, formattedResponse]
      messagesRef.current = finalMessages

      setState(prev => ({
        ...prev,
        messages: finalMessages,
        isLoading: false,
        suggestions: Array.isArray(formattedResponse.suggestions) ? formattedResponse.suggestions : []
      }))

    } catch (error) {
      console.error('Error listing missing requirements:', error)
      setState(prev => ({
        ...prev,
        error: 'Failed to list missing requirements',
        isLoading: false
      }))
    }
  }, [projectId, vendorResponseId])

  // Clear error
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }))
  }, [])

  return {
    // State
    ...state,
    suggestions: state.suggestions || defaultSuggestions,
    isConnected,
    
    // Actions
    sendMessage,
    clearConversation,
    draftFollowUpEmail,
    summarizeQualifications,
    listMissingRequirements,
    clearError,
    
    // Utilities
    hasMessages: state.messages && state.messages.length > 0,
    lastMessage: state.messages && state.messages[state.messages.length - 1] || null
  }
}